import * as React from 'react';

import { ContentCard } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { useBrokeredLoadFiles } from '../hooks/useBrokeredLoadFiles';

import { $remoteBrokeredLoadDetail } from '~/modules/brokerage/_state/brokeredLoadDetail.state';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { BrokeredLoadRecord } from '~/modules/brokerage/types/brokeredLoad';
import { FilesTab } from '~/shared/components/files/FilesTab';
import { GenericFile } from '~/shared/types/file';

type Props = {
  load: BrokeredLoadRecord;
};

export const BrokeredLoadTabPhotos: React.FC<Props> = ({ load }) => {
  const t = useTranslations();

  const fileMutations = useBrokeredLoadFiles();
  const { refetch } = useAtomValue($remoteBrokeredLoadDetail);

  const handleAllUploadsComplete = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const photoFiles = React.useMemo<GenericFile[]>(() => {
    if (!load.files) return [];

    return load.files
      .filter((file) => file.mimeType.startsWith('image/'))
      .map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        mimeType: file.mimeType,
        status: file.status,
        entityType: file.entityType,
        entityId: file.entityId,
        metadata: file.metadata,
        createdAt: file.createdAt?.toString(),
        updatedAt: file.updatedAt?.toString(),
      }));
  }, [load.files]);

  const dropZoneText = React.useMemo(
    () => ({
      primary: (
        <>
          Click <span className="font-semibold text-primary">here</span> or drag
          photos to upload
        </>
      ),
      secondary: 'Supports JPG, PNG, GIF, and other image formats',
    }),
    [],
  );

  return (
    <div className="@BrokeredLoadTabPhotos flex max-h-full flex-col gap-5 p-5">
      <ContentCard>
        <FilesTab
          entityId={load.id}
          files={photoFiles}
          mutations={fileMutations}
          onAllUploadsComplete={handleAllUploadsComplete}
          mimeType="image/*"
          dropZoneTheme="compact"
          dropZoneText={dropZoneText}
        />
        {photoFiles.length === 0 && (
          <div className="p-4 text-center text-neutral/60">
            {t.brokerage.loads.detail.photos.empty}
          </div>
        )}
      </ContentCard>
    </div>
  );
};
